// <booking-request-banner> Web Component
//
// Phase 1 of CDN caching refactor. Replaces the SSR-time `{{if .HasBookingRequest}}`
// conditional on /property/* with a client-rendered banner that appears for
// authenticated users who have a pending request for this property.
//
// Hidden by default in SSR; un-hides on /api/booking-requests/mine?property_id=X
// returning {has_request: true}. For anonymous users, stays hidden.
//
// Also hides matching `[data-when="no-pending-request"]` siblings within the
// same parent so the page doesn't show both "Request Booking" CTA + the
// "Already requested" banner simultaneously.
//
// Usage:
//   <booking-request-banner property-id="123" hidden class="block">
//     <div>...banner markup...</div>
//   </booking-request-banner>

(function () {
    class BookingRequestBanner extends HTMLElement {
        connectedCallback() {
            if (this._wiredOnce) return;
            this._wiredOnce = true;
            this._propertyId = this.getAttribute('property-id') || '';
            this._listenForUserChange();
            const self = this;
            const ready = window.userMenuReady || Promise.resolve(null);
            ready.then(function () {
                self._checkAndRender();
            });
        }

        _checkAndRender() {
            // Read latest cached value (NOT the once-resolved Promise — handles
            // bfcache / cross-tab logout scenarios where user state changed
            // after first hydration).
            if (!window.currentUser) {
                this._setBanner(false);
                return;
            }
            this._fetchAndApply();
        }

        _fetchAndApply() {
            if (!this._propertyId) return;
            const self = this;
            const url = '/api/booking-requests/mine?property_id=' + encodeURIComponent(this._propertyId);
            fetch(url, { cache: 'no-store', credentials: 'same-origin' })
                .then(function (resp) {
                    if (resp.status === 200) return resp.json();
                    if (resp.status === 401) return { has_request: false };
                    // 5xx or other non-2xx: don't update state. Returning
                    // null here would call _setBanner(false), which would
                    // SHOW the "Request Booking" CTA — a user with a real
                    // pending request might then double-request. Plan §1.5:
                    // "JS treats 500 same as transient network error
                    // (leave hidden, log to console)." Indeterminate result
                    // signaled by `null`.
                    return null;
                })
                .then(function (obj) {
                    if (obj === null) return; // indeterminate — leave SSR state intact
                    self._setBanner(!!obj.has_request);
                })
                .catch(function (err) {
                    console.error('booking-request-banner: fetch failed', err);
                    // Network error: leave SSR state intact (banner hidden,
                    // CTA visible — same as no-data path). Don't hide CTA
                    // since we genuinely can't verify there's a pending
                    // request.
                });
        }

        _setBanner(visible) {
            if (visible) {
                this.removeAttribute('hidden');
            } else {
                this.setAttribute('hidden', '');
            }
            // Toggle paired "Request Booking" CTA SIBLINGS (direct children
            // of the same parent only) so we don't render both states at
            // once. Scoped to direct children to avoid accidentally
            // toggling a nested unrelated `[data-when="no-pending-request"]`
            // somewhere deep in the parent's subtree.
            const parent = this.parentElement;
            if (!parent) return;
            for (let i = 0; i < parent.children.length; i++) {
                const el = parent.children[i];
                if (el === this) continue;
                if (el.getAttribute('data-when') !== 'no-pending-request') continue;
                if (visible) {
                    el.setAttribute('hidden', '');
                } else {
                    el.removeAttribute('hidden');
                }
            }
        }

        _listenForUserChange() {
            const self = this;
            document.addEventListener('user-changed', function () {
                self._checkAndRender();
            });
        }
    }

    if (!customElements.get('booking-request-banner')) {
        customElements.define('booking-request-banner', BookingRequestBanner);
    }
})();
